import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => localStorage.getItem('token');

const checkinApiClient = axios.create({
  baseURL: config.api.clientFormsServiceUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// All Annual Check-In admin routes are namespaced under /admin/checkin on
// the unified Client Forms service.
const ADMIN = '/admin/checkin';

checkinApiClient.interceptors.request.use(
  (cfg) => {
    const token = getToken();
    if (token) {
      cfg.headers['Authorization'] = `Bearer ${token}`;
    }
    return cfg;
  },
  (error) => Promise.reject(error),
);

applyAuthRetryInterceptor(checkinApiClient);
applyTokenRefreshInterceptor(checkinApiClient);

const checkinService = {
  fetchAllClients: async () => {
    const response = await checkinApiClient.get(`${ADMIN}/clients`);
    return response.data;
  },

  searchTamaracClients: async (query, limit = 25) => {
    const response = await checkinApiClient.get(`${ADMIN}/tamarac/clients`, {
      params: { query, limit },
    });
    return response.data?.clients || [];
  },

  searchTamaracClientsPA: async (query, maxResults = 25) => {
    const response = await checkinApiClient.get(`${ADMIN}/tamarac/clients/search-pa`, {
      params: { query, limit: maxResults },
      timeout: 45000,
    });
    return response.data?.clients || [];
  },

  fetchTamaracContactDetails: async (contactId) => {
    const response = await checkinApiClient.get(
      `${ADMIN}/tamarac/contacts/${encodeURIComponent(contactId)}/details`,
    );
    return response.data || {};
  },

  createClient: async (clientData) => {
    const response = await checkinApiClient.post(`${ADMIN}/clients`, clientData);
    return response.data;
  },

  deleteClient: async (clientId) => {
    const response = await checkinApiClient.delete(`${ADMIN}/clients/${clientId}`);
    return response.data;
  },

  resendLink: async (clientId) => {
    const response = await checkinApiClient.post(`${ADMIN}/clients/${clientId}/resend`);
    return response.data;
  },

  getLink: async (clientId) => {
    const response = await checkinApiClient.post(
      `${ADMIN}/clients/${clientId}/resend?send_notification=false`,
    );
    return response.data;
  },

  getClientDetail: async (clientId) => {
    const response = await checkinApiClient.get(`${ADMIN}/clients/${clientId}`);
    return response.data;
  },

  patchClient: async (clientId, fields) => {
    const response = await checkinApiClient.patch(`${ADMIN}/clients/${clientId}`, fields);
    return response.data;
  },

  scheduleSend: async (clientId, payload) => {
    const response = await checkinApiClient.post(
      `${ADMIN}/clients/${clientId}/schedule-send`,
      payload,
    );
    return response.data;
  },

  cancelScheduledSend: async (clientId) => {
    const response = await checkinApiClient.delete(
      `${ADMIN}/clients/${clientId}/schedule-send`,
    );
    return response.data;
  },

  emailSummaryPdf: async (clientId, payload) => {
    const response = await checkinApiClient.post(
      `${ADMIN}/clients/${clientId}/email-summary-pdf`,
      payload,
      { timeout: 60000 },
    );
    return response.data;
  },
};

export default checkinService;
